import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {
  GetDatasetColumns,
  GetDatasetColumnsVariables,
} from 'data-frames/graphql/__generated/GetDatasetColumns';
import {GET_DATASET_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {CreateStepParams} from 'pipelines/network/PipelineDAO';
import {getInputStepDatas, getOutputStepDatas} from 'pipelines/StepConnectionUtils';
import APIUtil from 'util/APIUtil';
import {JSONValue} from 'util/Types';

import {ParseClipboardContentFn} from './CopyPasteTypes';
import {parseApiIdsClipboardContent} from './parseApiIdsClipboardContent';

interface DatasetInfo {
  name: string;
  columns: Array<DataFrameColumnDef>;
}

async function loadDatasetInfo(datasetId: string): Promise<DatasetInfo | null> {
  const result = await getApolloClient().query<GetDatasetColumns, GetDatasetColumnsVariables>({
    query: GET_DATASET_COLUMNS_QUERY,
    variables: {datasetId},
  });
  const {dataset} = result.data;
  if (dataset.__typename === 'InaccessibleInfoNode') {
    return null;
  }
  const {dataFrame} = dataset;
  if (dataFrame.__typename === 'InaccessibleInfoNode') {
    return null;
  }
  return {
    name: dataFrame.name,
    columns: dataFrame.allColumnsJson as Array<DataFrameColumnDef>,
  };
}

function createColumnMappingConfig(columns: Array<DataFrameColumnDef>): Record<string, JSONValue> {
  return {
    keepUnmappedColumns: true,
    outputColumns: columns.map((column) => ({
      matchBy: 'NAME',
      caseSensitive: true,
      displayName: column.displayName,
      systemName: column.systemName,
      createEmptyColumnIfNotFound: false,
    })),
  };
}

/**
 * Create upsert for a DATA_CATALOG_DATASET step.
 */
function createDataCatalogDatasetStepUpsert(datasetId: string, dataFrameName: string): CreateStepParams {
  return {
    stepType: PipelineStepType.DATA_CATALOG_DATASET,
    stepVersion: 0,
    config: {datasetId, dataFrameName},
    incomingEdges: [],
    runAfterCreate: true,
  };
}

/**
 * Create upserts for a VARIABLE_TEMPLATE_INPUT_TABLE step and a APPLY_COLUMN_MAPPING step.
 *
 * Used in analysis templates, since variable input tables which don't require column mapping is the most
 * common way to use datasets.
 */
function createVariableTemplateInputTableSteps(
  datasetId: string,
  name: string,
  columns: Array<DataFrameColumnDef>,
  variableStepIndex: number
): Array<CreateStepParams> {
  const [outputStepData] = getOutputStepDatas(PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE);
  const [inputStepData] = getInputStepDatas(PipelineStepType.APPLY_COLUMN_MAPPING);

  return [
    {
      stepType: PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
      stepVersion: 0,
      config: {datasetId, name, description: '', requireColumnMapping: false},
      incomingEdges: [],
      runAfterCreate: true,
    },
    {
      stepType: PipelineStepType.APPLY_COLUMN_MAPPING,
      stepVersion: 0,
      config: createColumnMappingConfig(columns),
      incomingEdges: [
        {
          fromStepIndex: variableStepIndex,
          fromStepDataName: outputStepData.systemName,
          fromStepDataIndex: null,
          toStepDataName: inputStepData.systemName,
          toStepDataIndex: 0,
        },
      ],
    },
  ];
}

/** @package */
export const parseDatasetIdsClipboardContent: ParseClipboardContentFn = async (pipelineId, text) => {
  const datasetIds = parseApiIdsClipboardContent(text, ModelTypeConstants.DATASET);
  if (datasetIds == null) {
    return null;
  }

  // Launch all queries at once, so that they will be batched.
  const datasetInfos = await Promise.all(datasetIds.map((datasetId) => loadDatasetInfo(datasetId)));
  if (datasetInfos.some((datasetInfo) => datasetInfo == null)) {
    return null;
  }

  if (APIUtil.isAnalysisTemplate(pipelineId)) {
    return datasetInfos.flatMap((datasetInfo, index) => {
      const {name, columns} = datasetInfo!;
      return createVariableTemplateInputTableSteps(datasetIds[index], name, columns, index * 2);
    });
  }

  return datasetIds.map((datasetId, index) =>
    createDataCatalogDatasetStepUpsert(datasetId, datasetInfos[index]!.name)
  );
};
